import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CimaCore, Cloudinary, Formatter, Util, XmStore, XmStoreUtil } from 'core/services';
import { CmsLineDataPlanCollection, CmsPlanSwitch } from 'store/cms/models';
import { Subscription } from 'rxjs';
import { StateService } from '@uirouter/angular';

import { AccountSummary, LineSummary } from 'store/account-summary/models';
import { LineDataPlan, PlanEligibility } from 'store/line/models';

import { SwitchUtil } from '../../../shared/services/switch-util';
import { User } from 'store/user/models';
import { StoreAction } from 'store/actions';
import { DataPlanType, ProgressStatus, UnlPlanSubgroup, UnlPlanSubgroupName } from 'my-account/constants';
import { DiscountPromoCollection, DiscountPromotion } from 'store/promotions/discount-promotion/models';
import { IXMOptions } from 'core/interfaces';
import { CONFIG_TOKEN } from 'core/constants';
import { targetPlanSwitchDetails } from 'store/plan-eligibility/models/target-plan-switch';

type UnlimitedTierPlan = CmsUnlimitedTierPlan & LineDataPlan;

@Component({
    selector: 'xm-unlimited-tier-cancel-downgrade',
    templateUrl: './unlimited-tier-cancel-downgrade.html',
    styleUrls: ['./unlimited-tier-cancel-downgrade.scss'],
    standalone: false
})
export class XmPlanSwitchUnlimitedTierCancelDowngrade implements OnInit, OnDestroy {

    public pageData: CmsPlanSwitchUnlimitedTierUpgradeDownGrade;
    public user: User;
    public dataPlanCollection: CmsLineDataPlanCollection;
    public selectedPlanData: CmsDataPlanDetails;
    public lineSummary: LineSummary;
    public planEligibility: PlanEligibility;
    public eligiblePlanDetails: LineDataPlan;
    public isButtonEnabled: boolean = false;
    public primaryCtaText: string;
    public selectedPlan: LineDataPlan;
    public isBusinessUser: boolean;
    public isIntroPlanInUse: boolean = false;
    public isPlusPlanInUse: boolean = false;
    public isPremiumPlanInUse: boolean = false;
    public inEligibleInUsePlan: UnlimitedTierPlan;
    public tierData: UnlimitedTierPlan[];
    public discountPromoResp: DiscountPromoCollection;
    public showDeviceCreditPromo: boolean = false;
    public promoIcon: MediaImageOptions;
    public promotionHeader: string;
    public multiplePromotions: CmsPlanSwitchMultiplePromotions;
    public promotionData: string[] = [];
    public legacyPromoDetails: targetPlanSwitchDetails;
    public isIcopPlanSwitch: boolean;

    private subscriptions: Subscription[] = [];
    private xmStore: XmStore;
    private state: StateService;
    private cimaCore: CimaCore;
    private config: IXMOptions;
    private isCbmUserSegmentFiveUnlimitedPremium: boolean;
    private isCbmUserSegmentFiveTabletUnlimitedPremium: boolean;

    constructor(xmStore: XmStore, state: StateService, switchUtil: SwitchUtil, cimaCore: CimaCore, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { xmStore, state, switchUtil, cimaCore, config });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AccountSummary>(AccountSummary), (summary: AccountSummary) => {
            this.lineSummary = summary.getLineByKey(this.state.params.line_id);
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
            this.user = user;
            this.isCbmUserSegmentFiveUnlimitedPremium = this.isBusinessUser && this.user.account.isSegmentFive && this.lineSummary.isUnlPremium && !this.lineSummary.isTablet;
            this.isCbmUserSegmentFiveTabletUnlimitedPremium = this.isBusinessUser && this.user.account.isSegmentFive && this.lineSummary.isUnlPremium && this.lineSummary.isTablet;
            this.isIcopPlanSwitch = this.config.ENABLE_ICOP_P1_PLANSWITCH;
        }));

        XmStoreUtil.defaultCatch(this.xmStore.findPromise<DiscountPromoCollection>(StoreAction.GET_DISCOUNT_PROMOTION, DiscountPromoCollection))
            .then((response: DiscountPromoCollection) => {
                this.discountPromoResp = response;
                this.checkDeviceCreditPromo();
            });

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsPlanSwitch>(CmsPlanSwitch), (result: CmsPlanSwitch) => {
            this.promoIcon = this.generatePromotionIcon(result.dollarIcon);
            this.multiplePromotions = result.planSwitchMultiplePromotions; 

           
            if (this.user.account.isSegmentFive) {
                this.pageData = this.isBusinessUser ?
                    this.lineSummary.isTablet ? result.cbmPlanSwitchUnlimitedTabletTierUpgradeDowngrade : result.cbmPlanSwitchUnlimitedTierUpgradeDowngrade
                    : this.lineSummary.isTablet ? result.planSwitchUnlimitedTabletTierUpgradeDowngrade : result.planSwitchUnlimitedTierUpgradeDowngradeForSeg5;
            } else {
                this.pageData = this.isBusinessUser ?
                    this.lineSummary.isTablet ? result.cbmPlanSwitchUnlimitedTabletTierUpgradeDowngrade : result.cbmPlanSwitchUnlimitedTierUpgradeDowngrade
                    : this.lineSummary.isTablet ? result.planSwitchUnlimitedTabletTierUpgradeDowngrade : result.planSwitchUnlimitedTierUpgradeDowngradeForOtherSeg;
            }
            

            if (this.isCbmUserSegmentFiveUnlimitedPremium) {
                this.pageData = result.cbmPlanSwitchUnlimitedTierUpgradeDowngradeForSeg5;
            }

            if (this.isCbmUserSegmentFiveTabletUnlimitedPremium) {
                this.pageData = result.cbmPlanSwitchUnlimitedTierUpgradeDowngradeForSeg5Tablet;
            }
            
            this.primaryCtaText = this.pageData.switchCta;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
            this.dataPlanCollection = result;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<PlanEligibility>(PlanEligibility, {
            filters: { lineKey: this.state.params.line_id }
        }), (planEligibility: PlanEligibility) => {
            const planCode: string = decodeURIComponent(this.state.params.plan_code);
            this.planEligibility = planEligibility;
            this.eligiblePlanDetails = this.planEligibility.plans.find((plan: LineDataPlan) => plan.planCode === planCode);
        }));
        this.tierData = this.buildUnlTierObject(this.pageData.tiers, this.planEligibility.plans);

        // TODO: Use this peek to map icop promo content fot seg 4 and below users.
        if (this.isIcopPlanSwitch) {
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<targetPlanSwitchDetails>(targetPlanSwitchDetails), (legacyPromoDetails: targetPlanSwitchDetails) => {
                this.legacyPromoDetails = legacyPromoDetails;
            }));
        }
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public generatePromotionIcon(image: CmsMediaImage): MediaImageOptions {
        return {
            mobile: Cloudinary.generateMediaFromCms(image)
        };
    }

    public onEmitPlanCode(planCode: string): void {
        this.selectedPlan = this.filterUnlimitedTier(planCode, this.planEligibility.plans);
        this.updateButton(this.selectedPlan);
    }

    public onCancel(): void {
        this.state.go('account.activity.details', { line_id: this.state.params.line_id });
    }

    public shouldShowScheduledBadge(plan: LineDataPlan): boolean {
        return plan.isIneligibleScheduled;
    }

    public filterUnlimitedTier(currentPlanCode: string, plans: LineDataPlan[]): LineDataPlan {
        return plans.find((plan: LineDataPlan) => plan.planCode.includes(currentPlanCode));
    }

    public getPrice(plan: LineDataPlan): number {
        if (this.user.account.isSegmentFive && plan.isIneligibleInUse) {
            return plan.postSwitch.lineCost;
        } else if (this.user.account.isSegmentFive && !plan.isIneligibleInUse) {
            if (this.isCbmUserSegmentFiveUnlimitedPremium || this.isCbmUserSegmentFiveTabletUnlimitedPremium) {
                return plan.postSwitch.lineCost;
            } else  {
                return this.pageData.price;
            }
        } else {
            return plan.postSwitch.lineCost;
        }
    }

    public showPriceSymbol(): string {
        if (this.user.account.isSegmentFive) {
            if (this.planEligibility.plans[0].isIneligibleScheduled) {
                return this.pageData.downgradePriceSymbol;
            } else if (this.planEligibility.plans[1].isIneligibleScheduled) {
                return this.pageData.upgradePriceSymbol;
            } else {
                return '';
            }
        } else {
            return '';
        }
    }

    public onSubmit(): void {
        const postApiAction: string = StoreAction.POST_CANCEL_PLAN_SCHEDULED_DOWNGRADE_VIA_SSM_PROXY;
        this.xmStore.persist(postApiAction, {
            lineKey: this.state.params.line_id,
            planId: this.selectedPlan.planCode
        }).then(() => {
            if (this.selectedPlan.isIneligibleInUse) {
                this.clearModel();
                let action: string;

                if (this.config.ENABLE_CBM_GEMINI && this.cimaCore.isBusinessUser && this.user.account.isNoLevel) {
                    action = StoreAction.GET_SSM_ACCOUNT_SUMMARY_CBM_NO_LEVEL;
                } else if (this.user.account.isSegmentSix && !this.cimaCore.isBusinessUser) {
                    action = StoreAction.GET_ACCOUNT_SUMMARY_FOR_PLAN_EIGHT;
                } else if (this.user.account.isSegmentFive || this.user.account.isSegmentSix) {
                    action = StoreAction.GET_ACCOUNT_SUMMARY_VIA_SSM_PROXY;
                } else {
                    action = StoreAction.GET_ACCOUNT_SUMMARY;
                }

                this.xmStore.fetchPromise<AccountSummary>(action).then(() => {
                    this.state.go('account.activity.details.plan-switch.success', { line_id: this.state.params.line_id, plan_code: this.selectedPlan.planCode, status: ProgressStatus.SUCCESS });
                });
            } else {
                this.state.go('account.activity.details.plan-switch.in-progress', { line_id: this.state.params.line_id, plan_code: this.selectedPlan.planCode });
            }
        }).catch(() => {
            this.state.go('account.activity.details.plan-switch.failure', { line_id: this.state.params.line_id, plan_code: this.selectedPlan.planCode, status: ProgressStatus.FAIL });
        });
    }

    public showPlanNameHeader(): string {
        return this.selectedPlanData ? this.selectedPlanData.planType : DataPlanType.UNLIMITED;
    }

    public showInUseBadge(tier: UnlimitedTierPlan): boolean {
        if (tier.eligibilityStatus === 'INELIGIBLE_IN_USE') {
            this.selectedPlanData = this.dataPlanCollection.plans.find((cmsPlan: CmsDataPlanDetails) => cmsPlan.planCode.includes(tier.planCode));
            this.getPlanUsageStatus(tier.planCode);
        
            this.replaceContent();
            
            this.inEligibleInUsePlan = tier;
            if (this.lineSummary.isTablet) {
                this.inEligibleInUsePlan.name = (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.TABLET_INTRO) ? UnlPlanSubgroupName.TABLET_INTRO
                    : (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.TABLET_PREMIUM) ? UnlPlanSubgroupName.TABLET_PREMIUM
                        : this.inEligibleInUsePlan.name;
            } else {
                this.inEligibleInUsePlan.name = (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.INTRO) ? UnlPlanSubgroupName.INTRO
                    : (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.PREMIUM) ? UnlPlanSubgroupName.PREMIUM
                        : this.inEligibleInUsePlan.name;
            }
        }

        return tier.isIneligibleInUse;
    }

    private setPromotionData(): void {
        this.discountPromoResp.mvnoPromotions.forEach((promo: DiscountPromotion) => {
            if ((promo.lineId === this.lineSummary.id.value || promo.lineId === this.lineSummary.id.key) && promo.isTradeInDDAPromoEligible) {
                this.promotionData.push(this.getPromoAmount(this.multiplePromotions.unlTiersDeviceCreditsPromotions.WithTradeIn, promo));
            } else if ((promo.lineId === this.lineSummary.id.value || promo.lineId === this.lineSummary.id.key) && promo.isExtendedPromoDollarEligible) {
                this.promotionData.push(this.getPromoAmount(this.multiplePromotions.unlTiersDeviceCreditsPromotions.extendedPromo, promo));
            }
        });
        if (this.promotionData.length > 0) {
            this.promotionHeader = (this.discountPromoResp.isTradeInDDAPromoEligible && 
                (this.inEligibleInUsePlan.name === UnlPlanSubgroupName.PLUS || this.inEligibleInUsePlan.name === UnlPlanSubgroupName.PREMIUM || this.inEligibleInUsePlan.name === UnlPlanSubgroupName.TABLET_PREMIUM)) ? 
                this.multiplePromotions.unlTiersDeviceCreditsPromotions.generalDowngrade : this.multiplePromotions.unlTiersDeviceCreditsPromotions.general;
        }
    }

    private checkDeviceCreditPromo(): void {
        if (this.discountPromoResp && this.discountPromoResp.mvnoPromotions.length > 0) {
            this.discountPromoResp.mvnoPromotions.forEach((promo: DiscountPromotion) => {
                if (promo.isTradeInDDAPromoEligible || promo.isExtendedPromoDollarEligible &&  this.user.account.isSegmentFive ||  this.user.account.isSegmentFour) {
                    this.showDeviceCreditPromo = true;
                }
            });
        }
        
        if (this.showDeviceCreditPromo) {
            this.setPromotionData();
        }
    }

    private getPromoAmount(description: string, promoResponse: DiscountPromotion): string {
        const amount: string = promoResponse.billerInfo.promotionAmount.toString();
        
        return description.replace('[AMOUNT]', amount);
    }

    private capitalizeWord(value: string): string {
        return value === UnlPlanSubgroup.BASIC ? 'Intro' : value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    }

    private clearModel(): void {
        let action: string;

        if (this.config.ENABLE_CBM_GEMINI && this.cimaCore.isBusinessUser && this.user.account.isNoLevel) {
            action = StoreAction.GET_SSM_ACCOUNT_SUMMARY_CBM_NO_LEVEL;
        } else if (this.user.account.isSegmentSix && !this.cimaCore.isBusinessUser) {
            action = StoreAction.GET_ACCOUNT_SUMMARY_FOR_PLAN_EIGHT;
        } else if (this.user.account.isSegmentFive || this.user.account.isSegmentSix) {
            action = StoreAction.GET_ACCOUNT_SUMMARY_VIA_SSM_PROXY;
        } else {
            action = StoreAction.GET_ACCOUNT_SUMMARY;
        }

        const clearApisList: StoreDeleteAction[] = [
            { action: StoreAction.CLEAR_LINE_LEVEL_PLAN_ELIGIBILITY },
            { action }
        ];

        this.xmStore.clearApis(clearApisList);
    }

    private getPlanUsageStatus(planCode: string): void {
        const planCodeIntro: string = this.pageData.tiers.find((tier: CmsUnlimitedTierPlan) => tier.name === UnlPlanSubgroupName.INTRO)?.radioOption.value;
        const planCodePlus: string = this.pageData.tiers.find((tier: CmsUnlimitedTierPlan) => tier.name === UnlPlanSubgroupName.PLUS)?.radioOption?.value;
        const planCodePremium: string = this.pageData.tiers.find((tier: CmsUnlimitedTierPlan) => tier.name === UnlPlanSubgroupName.PREMIUM)?.radioOption?.value;
        this.isIntroPlanInUse = planCode.startsWith(planCodeIntro);
        this.isPlusPlanInUse = planCode.startsWith(planCodePlus);
        this.isPremiumPlanInUse = planCode.startsWith(planCodePremium);
    }

    private replaceContent(): void {
        if (this.user.account.isSegmentFive) {
            this.pageData.description = this.isPlusPlanInUse ? this.pageData.inUsePlusDescription : this.pageData.description;
        } else {
            this.pageData.description = this.isPremiumPlanInUse ? this.pageData.inUsePremiumDescription : this.pageData.description;
        }

        if (this.isCbmUserSegmentFiveUnlimitedPremium || this.isCbmUserSegmentFiveTabletUnlimitedPremium) {
            this.pageData.description = this.pageData.inUsePremiumDescription;
            this.pageData.description = this.pageData.description.replace('[DATE]', Formatter.formatMonthDateYear(this.lineSummary?.scheduledSwitch?.scheduleDate));
        }

        this.pageData.description = this.pageData.description
            .replace('[DATE]', Formatter.formatMonthDateYear(this.lineSummary?.scheduledSwitch?.scheduleDate))
            .replace('[PLAN_SUBGROUP]', this.capitalizeWord(this.lineSummary?.scheduledSwitch?.planSubgroup));
    }

    private updateButton(selectedPlan: LineDataPlan): void {
        if (selectedPlan.isIneligibleInUse) {
            this.primaryCtaText = this.pageData.cancelDowngradeCta;
            this.isButtonEnabled = true;
        } else if (selectedPlan.isIneligibleScheduled) {
            this.primaryCtaText = this.pageData.scheduledSwitchCta;
            this.isButtonEnabled = false;
        } else {
            this.primaryCtaText = this.selectedPlanData.planName === UnlPlanSubgroupName.PREMIUM ? this.pageData.scheduledSwitchCta : this.pageData.switchCta;
            this.isButtonEnabled = true;
        }
    }

    private buildUnlTierObject(cmsTiers: CmsUnlimitedTierPlan[], plans: LineDataPlan[]): UnlimitedTierPlan[] {
        const tierData: UnlimitedTierPlan[] = [];
        cmsTiers.map((tier: CmsUnlimitedTierPlan) => {
            if (plans.some((plan: LineDataPlan) => plan.planCode.startsWith(tier.radioOption.value))) {
                const currentPlan: LineDataPlan = plans.find((plan: LineDataPlan) => plan.planCode.startsWith(tier.radioOption.value));
                // eslint-disable-next-line prefer-object-spread
                tierData.push(Object.assign({}, tier, currentPlan));
            }
        });

        return tierData;
    }
}
